import Choices from "choices.js";
import algoliasearch from "algoliasearch";
import {showElement} from "./utils";
import orderBy from "lodash.orderby";

const initSearchWidget = () => {
  const element = document.getElementById("search_widget");
  if (!element) {
    return;
  }
  const searchWidget = new Choices(element, {
    removeItemButton: true,
    maxItemCount: 40,
    searchFloor: 0,
    searchChoices: false,
    duplicateItems: false,
    shouldSort: false,
    itemSelectText: "",
    position: "bottom",
    classNames: {
      containerOuter: [
        "choices",
        "flex",
        "flex-column",
        "search-widget-container"
      ].join(" "),
      containerInner: [
        "flex-auto",
        "pt2",
        "pb1",
        "ph2",
        "br1",
        "br--left",
        "f6",
        "bg-white",
        "black",
        "overflow-hidden",
        "cursor-text",
        "search-input-container"
      ].join(" "),
      item: [
        "dib",
        "mb1",
        "mr1",
        "pa1",
        "br1",
        "ba",
        "b--moon-gray",
        "bg-light-gray",
        "black",
        "f6",
        "lh-copy"
      ].join(" "),
      itemChoice: [
        "pa2",
        "bn",
        "bg-white",
        "hover-bg-near-white",
        "autocomplete-item"
      ].join(" "),
      itemSelectable: ["pointer", "search-item-selectable"].join(" "),
      input: ["dib"].join(" "),
      inputCloned: [
        "v-base",
        "bn",
        "br0",
        "pt1",
        "pr0",
        "pb1",
        "mw-100",
        "bg-white",
        "f5",
        "search-input"
      ].join(" "),
      button: [
        "dib",
        "mv0",
        "ml1",
        "pv0",
        "pl1",
        "pr0",
        "bn",
        "outline-0",
        "lh-solid",
        "bg-transparent",
        "bg-center",
        "pointer",
        "dim",
        "remove-search-token-button"
      ].join(" ")
    }
  }).setValue(window.tokens);

  const buttonContainer = document.getElementById("search_button_container");
  const laboratorySelect = document.getElementById("laboratory_select");
  const wrapper = document.createElement("div");
  wrapper.classList.add("flex", "flex-row");
  searchWidget.containerOuter.insertBefore(
    wrapper,
    searchWidget.containerInner
  );
  wrapper.appendChild(searchWidget.containerInner);
  if (laboratorySelect) {
    wrapper.appendChild(laboratorySelect);
  }
  wrapper.appendChild(buttonContainer);
  const searchWidgetContainer = document.getElementById(
    "search_widget_container"
  );
  searchWidgetContainer.classList.remove("dn");

  const updateQueryInput = () => {
    const searchValue = searchWidget.getValue(true).join("+");
    const queryInput = document.getElementById("q");
    queryInput.value = searchValue;
  };

  const formatMostRelevantHighlight = (highlight, hitKind, laboratory) => {
    if (laboratory === "quest" && hitKind === "test") {
      switch (highlight.attributeName) {
        case "ntc_code":
          return `[ntc:${highlight.value}]`;
        case "quest_alternative_test_names":
          return `[a.k.a. ${highlight.value}]`;
        case "quest_component_result_names":
        case "quest_component_medical_names":
        case "quest_component_synonyms_through_tests":
          return `[includes ${highlight.value}]`;
        case "lto_condition_titles":
        case "lto_condition_keywords":
          return `[condition ${highlight.value}]`;
      }
    }

    if (laboratory === "quest" && hitKind === "component") {
      switch (highlight.attributeName) {
        case "loinc":
          return `[loinc:${highlight.value}]`;
        case "quest_medical_name":
          return `[a.k.a. ${highlight.value}]`;
        case "quest_test_names":
        case "quest_alternative_test_names":
          return `[in ${highlight.value}]`;
        case "lto_condition_titles":
        case "lto_condition_keywords":
          return `[condition ${highlight.value}]`;
      }
    }

    if (laboratory === "labcorp" && hitKind === "test") {
      switch (highlight.attributeName) {
        case "labcorp_test_code":
          return `[test code:${highlight.value}]`;
        case "labcorp_test_synonyms":
        case "labcorp_test_synonyms_through_components":
          return `[a.k.a. ${highlight.value}]`;
        case "labcorp_test_readable_component_names":
        case "labcorp_component_result_names":
          return `[includes ${highlight.value}]`;
        case "lto_condition_titles":
        case "lto_condition_keywords":
          return `[condition ${highlight.value}]`;
      }
    }

    if (laboratory === "labcorp" && hitKind === "component") {
      switch (highlight.attributeName) {
        case "loinc":
          return `[loinc:${highlight.value}]`;
        case "labcorp_test_readable_component_names":
          return `[a.k.a. ${highlight.value}]`;
        case "labcorp_test_names":
        case "labcorp_test_synonyms":
          return `[in ${highlight.value}]`;
        case "lto_condition_titles":
        case "lto_condition_keywords":
          return `[condition ${highlight.value}]`;
      }
    }
  };

  const formatHit = (
    hit,
    laboratory,
    hitKind,
    mainAttributeName,
    attributesToHighlight,
    query
  ) => {
    const mainAttributeHighlight = hit._highlightResult[mainAttributeName];
    let labelSuffix = "";

    if (mainAttributeHighlight.matchLevel === "none" && query !== "") {
      const highlights = [];

      for (let attributeName in hit._highlightResult) {
        if (hit._highlightResult.hasOwnProperty(attributeName)) {
          const highlight = hit._highlightResult[attributeName];
          if (Array.isArray(highlight)) {
            highlight.forEach(nestedHighlight => {
              highlights.push(
                Object.assign({}, nestedHighlight, {attributeName})
              );
            });
          } else {
            highlights.push(Object.assign({}, highlight, {attributeName}));
          }
        }
      }

      const filteredHighlights = highlights
        .filter(highlight => highlight.attributeName !== mainAttributeName)
        .filter(highlight => highlight.matchLevel === "full");

      const mostRelevantHighlight = orderBy(
        filteredHighlights,
        [
          highlight => attributesToHighlight.indexOf(highlight.attributeName),
          highlight => {
            if (highlight.fullyHighlighted) {
              return 0;
            }
            return 1;
          }
        ],
        ["asc", "asc"]
      )[0];

      if (hit._rankingInfo.words === 0) {
        labelSuffix = " [popular]";
      } else if (mostRelevantHighlight) {
        labelSuffix =
          " " +
          formatMostRelevantHighlight(
            mostRelevantHighlight,
            hitKind,
            laboratory
          );
      }
    }

    return {
      label: `${mainAttributeHighlight.value} (${hitKind})${labelSuffix}`,
      value: hit.internal_search_id
    };
  };

  const algoliaClient = algoliasearch(
    window.algolia_settings.application_id,
    window.algolia_settings.api_key
  );

  const performAlgoliaSearch = () => {
    const query = searchWidget.input.value;

    const filters = [];

    if (window.tokens) {
      filters.push(
        ...window.tokens.map(item => `(NOT internal_search_id:${item.value})`)
      );
    }

    let testIndexName;
    let componentIndexName;
    let testAttributesToHighlight;
    let componentAttributesToHighlight;
    let mainTestAttributeName;
    let mainComponentAttributeName;

    const laboratory = laboratorySelect.value;

    if (laboratory === "quest") {
      testIndexName = window.algolia_settings.quest_tests_index_name;
      componentIndexName = window.algolia_settings.quest_components_index_name;
      mainTestAttributeName = "quest_test_name";
      mainComponentAttributeName = "quest_result_name";
      testAttributesToHighlight = [
        "quest_test_name",
        "ntc_code",
        "quest_alternative_test_names",
        "quest_component_result_names",
        "quest_component_synonyms_through_tests",
        "quest_clinical_significance",
        "lto_condition_titles",
        "lto_condition_keywords",
        "quest_component_medical_names"
      ];
      componentAttributesToHighlight = [
        "quest_result_name",
        "loinc",
        "quest_medical_name",
        "quest_test_names",
        "quest_alternative_test_names",
        "lto_condition_titles",
        "lto_condition_keywords"
      ];
    } else {
      testIndexName = window.algolia_settings.labcorp_tests_index_name;
      componentIndexName =
        window.algolia_settings.labcorp_components_index_name;
      mainTestAttributeName = "labcorp_test_name";
      mainComponentAttributeName = "labcorp_component_result_name";
      testAttributesToHighlight = [
        "labcorp_test_name",
        "labcorp_test_code",
        "labcorp_test_synonyms",
        "labcorp_test_readable_component_names",
        "labcorp_test_synonyms_through_components",
        "labcorp_component_result_names",
        "lto_condition_titles",
        "lto_condition_keywords"
      ];
      componentAttributesToHighlight = [
        "labcorp_component_result_name",
        "loinc",
        "labcorp_test_readable_component_names",
        "labcorp_test_synonyms",
        "labcorp_test_names",
        "lto_condition_titles",
        "lto_condition_keywords"
      ];
    }

    const algoliaQueries = [
      {
        indexName: testIndexName,
        query: query,
        params: {
          hitsPerPage: 5,
          attributesToHighlight: testAttributesToHighlight,
          attributesToRetrieve: ["internal_search_id"],
          getRankingInfo: true,
          filters: filters.join(" AND ")
        }
      },
      {
        indexName: componentIndexName,
        query: query,
        params: {
          hitsPerPage: 1,
          attributesToHighlight: componentAttributesToHighlight,
          attributesToRetrieve: ["internal_search_id"],
          getRankingInfo: true,
          filters: filters.join(" AND ")
        }
      }
    ];

    algoliaClient.search(algoliaQueries).then(response => {
      const testHits = response.results[0].hits.map(hit => {
        return formatHit(
          hit,
          laboratory,
          "test",
          mainTestAttributeName,
          testAttributesToHighlight,
          query
        );
      });
      const componentHits = response.results[1].hits.map(hit => {
        return formatHit(
          hit,
          laboratory,
          "component",
          mainComponentAttributeName,
          componentAttributesToHighlight,
          query
        );
      });
      searchWidget.setChoices(
        testHits.concat(componentHits),
        "value",
        "label",
        true
      );
    });
  };

  updateQueryInput();
  performAlgoliaSearch();

  if (laboratorySelect) {
    laboratorySelect.addEventListener("mousedown", event =>
      event.stopPropagation()
    );
    laboratorySelect.addEventListener("mouseup", event =>
      event.stopPropagation()
    );
    laboratorySelect.addEventListener("click", event =>
      event.stopPropagation()
    );
    laboratorySelect.addEventListener("change", () => {
      updateQueryInput();
      performAlgoliaSearch();
    });
  }

  element.addEventListener("search", performAlgoliaSearch);

  element.addEventListener("change", event => {
    searchWidget.hideDropdown();
    updateQueryInput();
    event.target.form.submit();
  });

  const verboseCheckbox = document.getElementById("verbose");
  if (verboseCheckbox) {
    verboseCheckbox.addEventListener("change", event => {
      const verboseInput = document.querySelector("[name=verbose]");
      if (verboseCheckbox.checked) {
        verboseInput.value = "1";
      } else {
        verboseInput.value = "0";
      }
      event.target.form.submit();
    });
  }

  const maxStoresSelect = document.getElementById("max_stores");
  if (maxStoresSelect) {
    maxStoresSelect.addEventListener("change", event => {
      event.target.form.submit();
    });
  }

  const searchButton = document.getElementById("search_button");
  searchButton.addEventListener("click", event => {
    event.stopPropagation();
    const tokens = searchWidget.getValue(true);
    if (tokens.length > 0) {
      event.target.form.submit();
    } else {
      showElement(document.getElementById("autocomplete_warning"));
    }
  });
};

if (document.readyState === "complete") {
  initSearchWidget();
} else {
  document.addEventListener("DOMContentLoaded", initSearchWidget, false);
}



// WEBPACK FOOTER //
// ./js/searchWidget.js